// The words the plugin panels show, and the small readings behind them.
//
// These are the words a person sees in Nucleus (readme_library/plugin-words.md),
// so a developer reads their plugin the way people will: Done / Partial /
// Failed / Refused / Waiting for you / Skipped / Cancelled, Started by, Runs
// in, Left out, Settings (asked once) and Inputs (asked each run).

// ── Run outcomes ────────────────────────────────────────────────────────────

export const OUTCOMES = ['done', 'partial', 'failed', 'refused', 'waiting', 'skipped', 'cancelled'];

const OUTCOME_WORD = {
  done: 'Done',
  partial: 'Partial',
  failed: 'Failed',
  refused: 'Refused',
  waiting: 'Waiting for you',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
  unknown: 'Unknown',
  running: 'Running',
};

export const outcomeWord = (outcome) => OUTCOME_WORD[outcome] || OUTCOME_WORD.unknown;

/** A tone for an outcome's mark: ok, warn, bad or quiet. */
export function outcomeTone(outcome) {
  if (outcome === 'done') return 'ok';
  if (outcome === 'partial' || outcome === 'waiting' || outcome === 'running') return 'warn';
  if (outcome === 'failed' || outcome === 'refused') return 'bad';
  return 'quiet';
}

// ── Where a run was refused ─────────────────────────────────────────────────

const STAGE_WORD = {
  reach: 'It isn’t one they can reach',
  tool: 'That tool isn’t there',
  install: 'It isn’t set up',
  approval: 'It isn’t turned on, or the app has no access to the tool',
  parameters: 'A setting has no value',
  grant: 'Its realm doesn’t let it run',
  inputs: 'An input has no value',
  remove_source: 'It may not delete what it read',
  consent: 'It needed an Allow nobody gave',
  paused: 'The plugin is Off',
  call: 'Your server said no',
};

const humanise = (s) => {
  const t = String(s || '').replace(/_/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

/** "It isn't set up", from a stage name. Unknown stages read as words. */
export const stageWord = (stage) => (stage ? STAGE_WORD[stage] || humanise(stage) : null);

/**
 * A refusal ({stage, code, text}) as { stage, text, code }: the server's own
 * words when it sent them, else the stage's. Null when there was none.
 */
export function refusalOf(refused) {
  if (!refused || typeof refused !== 'object') return null;
  const stage = refused.stage || refused.refused_at || null;
  const text = refused.text || stageWord(stage) || 'Refused';
  return { stage, stageText: stageWord(stage), text, code: refused.code || null };
}

// ── What a run left out ─────────────────────────────────────────────────────

const LEFT_OUT_WORD = {
  outside_grant: 'Outside the realm it ran in',
  reserved_schema: 'Of a schema only ofSelf writes',
  invalid_reference: 'Pointing at a node it may not use',
  not_declared: 'Not something it says it writes',
  not_an_input: 'On a node it was not given',
  not_found: 'On a node that is not there',
  malformed: 'Not in a shape ofSelf reads',
  invalid: 'Not a valid value',
  nothing_to_propose: 'Nothing to propose',
  already_proposed: 'Already proposed',
};

export const leftOutWord = (why) => LEFT_OUT_WORD[why] || humanise(why);

const list = (x) => (Array.isArray(x) ? x : []);

/**
 * What a run did, from its output_summary: { made, changed, removed, proposed,
 * leftOut: [{ word, count }] }. `cannot_write` (what its realm did not let it
 * write) reads as a Left out line too.
 */
export function runCounts(summary) {
  const s = summary && typeof summary === 'object' ? summary : {};
  const made = typeof s.output_count === 'number' ? s.output_count : list(s.output_node_ids).length;
  const leftOut = Object.entries(s.outputs_dropped || {})
    .filter(([, c]) => c > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([why, count]) => ({ word: leftOutWord(why), count }));
  list(s.cannot_write).filter(Boolean).forEach((what) => {
    leftOut.push({ word: `Its realm doesn’t let it ${what}`, count: null });
  });
  return {
    made,
    changed: list(s.nodes_edited).length || (typeof s.changed_count === 'number' ? s.changed_count : 0),
    removed: list(s.nodes_deleted).length,
    proposed: list(s.proposals_created).length || (typeof s.proposed_count === 'number' ? s.proposed_count : 0),
    leftOut,
  };
}

/** "Made 2 · changed 1", or null when it did nothing countable. */
export function countsLine(counts) {
  const c = counts || {};
  const parts = [
    c.made > 0 && `Made ${c.made}`,
    c.changed > 0 && `changed ${c.changed}`,
    c.removed > 0 && `removed ${c.removed}`,
    c.proposed > 0 && `proposed ${c.proposed}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

const ACTION_WORD = {
  create: 'Create', update: 'Change', edit: 'Change', delete: 'Remove', remove: 'Remove',
  propose: 'Propose', link: 'Link',
};
/** A write's verb as a word: "Create", "Change". */
export const actionWord = (action) => ACTION_WORD[action] || humanise(action) || 'Write';

// ── Who started a run ───────────────────────────────────────────────────────

const SOURCE_WORD = {
  manual: 'You', press: 'You', canvas: 'You', replay: 'You', schedule: 'Runs automatically',
  event: 'An automation', flow: 'A flow', app: 'An app', agent: 'An agent', test: 'Test run',
};

/**
 * The server's own name for who started it, else one made from the row. A push
 * (the plugin's own service sending data in) is started by the plugin.
 */
export function startedByWord(run, pluginName) {
  const by = run?.started_by;
  if (by?.name) return by.name;
  if (run?.trigger_source === 'push') return pluginName || 'The plugin';
  return SOURCE_WORD[run?.trigger_source] || '—';
}

// ── Fields: Settings (asked once) and Inputs (asked each run) ────────────────

// A field with no scope is asked once, at setup — the backend's default.
export const scopeOf = (param) => (param?.scope === 'runtime' ? 'runtime' : 'setup');
export const scopeWord = (scope) => (scope === 'runtime' ? 'Inputs' : 'Settings');

/**
 * The tools a test can pick from. A plugin with no tools is one tool: itself,
 * with the plugin's own fields and no tool id.
 */
export function testableTools(plugin, registered) {
  const tools = list(registered).length ? list(registered) : list(plugin?.tools);
  if (tools.length) {
    return tools.map((t) => ({
      tool_id: t.tool_id ?? null,
      name: t.name || t.tool_id || 'Tool',
      description: t.description || '',
      input_parameters: list(t.input_parameters),
      effects: list(t.effects),
    }));
  }
  return [{
    tool_id: null,
    name: plugin?.name || 'This plugin',
    description: plugin?.description || '',
    input_parameters: list(plugin?.input_parameters),
    effects: list(plugin?.effects),
  }];
}

/** A tool's fields split by scope: { inputs, settings }. */
export function fieldsOf(tool) {
  const params = list(tool?.input_parameters).filter((p) => p && p.key);
  return {
    inputs: params.filter((p) => scopeOf(p) === 'runtime'),
    settings: params.filter((p) => scopeOf(p) === 'setup'),
  };
}

/** The value a field starts at in the test form. */
export function initialValue(param) {
  if (param?.default !== undefined && param.default !== null) {
    if (param.type === 'multiselect') return list(param.default);
    if (param.type === 'boolean') return Boolean(param.default);
    return String(param.default);
  }
  if (param?.type === 'boolean') return false;
  if (param?.type === 'multiselect') return [];
  return '';
}

/**
 * A form value as the type the field declares: { value } or { error }. An
 * empty value is { value: undefined } — left out of the body, not sent blank.
 */
export function coerceValue(param, raw) {
  const type = param?.type || 'text';
  if (type === 'boolean') return { value: Boolean(raw) };
  if (type === 'multiselect') {
    const v = list(raw);
    return { value: v.length ? v : undefined };
  }
  const s = raw === undefined || raw === null ? '' : String(raw).trim();
  if (s === '') return { value: undefined };
  if (type === 'integer') {
    if (!/^-?\d+$/.test(s)) return { error: 'a whole number' };
    return { value: parseInt(s, 10) };
  }
  if (type === 'float' || type === 'number') {
    const n = Number(s);
    if (!Number.isFinite(n)) return { error: 'a number' };
    return { value: n };
  }
  if (type === 'select') {
    // A select's options may be numbers; send the option itself.
    const opt = list(param.options).map((o) => (o && typeof o === 'object' ? o.value : o))
      .find((o) => String(o) === s);
    return { value: opt !== undefined ? opt : s };
  }
  return { value: type === 'textarea' ? String(raw) : s };
}

/** A field's name as people see it: its label, never its key. */
export const fieldLabel = (param) => param?.label || humanise(param?.key);

/**
 * The test-run body from the form: { body, errors }. `values` is keyed by
 * field key. Secret fields are never sent from here; a test uses your own
 * saved credentials.
 */
export function buildTestBody(tool, values) {
  const { inputs, settings } = fieldsOf(tool);
  const errors = {};
  const collect = (params) => {
    const out = {};
    params.filter((p) => !p.secret).forEach((p) => {
      const { value, error } = coerceValue(p, values?.[p.key]);
      if (error) errors[p.key] = `${fieldLabel(p)} must be ${error}.`;
      else if (value === undefined) {
        if (p.required && p.type !== 'boolean') errors[p.key] = `${fieldLabel(p)} needs a value.`;
      } else out[p.key] = value;
    });
    return out;
  };
  const body = { tool_id: tool?.tool_id ?? null, input_parameters: collect(inputs) };
  const s = collect(settings);
  if (Object.keys(s).length) body.settings = s;
  return { body, errors };
}

// ── Health ──────────────────────────────────────────────────────────────────

/** A connection error in plain words. */
export function plainError(error) {
  const e = String(error || '');
  if (!e) return null;
  if (/does not resolve|nodename nor servname|Name or service not known|getaddrinfo/i.test(e)) {
    return 'The address doesn’t resolve. No such host.';
  }
  if (/timed? ?out|timeout/i.test(e)) return 'It didn’t answer within 5 seconds.';
  if (/refused/i.test(e)) return 'Nothing is listening at that address.';
  if (/certificate|ssl|tls/i.test(e)) return 'Its certificate isn’t valid.';
  if (/private|loopback|blocked|not allowed|egress/i.test(e)) return 'That address is private, so ofSelf won’t call it.';
  return e;
}

/**
 * The health check as { tone, word, detail, raw }. `ms` is the round trip the
 * portal measured.
 */
export function healthView(health, ms) {
  if (!health) return null;
  const took = typeof ms === 'number' ? ` in ${Math.round(ms)} ms` : '';
  if (health.status === 'ok') {
    const code = health.http_status ? ` ${health.http_status}` : '';
    return { tone: 'ok', word: 'Reachable', detail: `Answered${code}${took}`, raw: null };
  }
  if (health.status === 'error') {
    return { tone: 'bad', word: 'Answers with an error', detail: `HTTP ${health.http_status}${took}`, raw: null };
  }
  if (health.status === 'unreachable') {
    return { tone: 'bad', word: 'Not reachable', detail: plainError(health.error), raw: health.error || null };
  }
  if (health.status === 'no_endpoint') {
    const msg = health.message ? `${String(health.message).replace(/\.$/, '')}.` : null;
    return { tone: 'quiet', word: 'Nothing to check', detail: msg, raw: null };
  }
  return { tone: 'quiet', word: humanise(health.status) || 'Unknown', detail: health.message || null, raw: null };
}

// ── Dates ───────────────────────────────────────────────────────────────────

export function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Verification ────────────────────────────────────────────────────────────

/** { state, label } from the plugin's `verification` (or `is_verified`). */
export function verificationView(plugin) {
  const v = plugin?.verification || {};
  const state = v.state || (plugin?.is_verified ? 'verified' : 'not_requested');
  if (state === 'verified') {
    const on = formatDate(v.verified_at || plugin?.verified_at);
    return { state, label: 'Verified by ofSelf', date: on };
  }
  if (state === 'requested') {
    const on = formatDate(v.requested_at);
    return { state, label: on ? `Requested on ${on}` : 'Requested', date: on };
  }
  return { state: 'not_requested', label: 'Not requested', date: '' };
}

// ── Publish, and turning it on again ─────────────────────────────────────────────

export const distributionOf = (plugin) => (plugin?.deployment_mode === 'public' ? 'public' : 'incubator');

/** The PUT body for a distribution. */
export function distributionBody(mode, allowedUserIds) {
  if (mode === 'public') {
    return { deployment_mode: 'public', app_store_visibility: 'all_users', allowed_user_ids: [] };
  }
  return {
    deployment_mode: 'incubator',
    app_store_visibility: 'selected_users',
    allowed_user_ids: [...new Set(list(allowedUserIds).filter(Boolean))],
  };
}

/** "3 people and groups review what changed and turn it on again." */
export function reapprovalLine(n) {
  const count = Number(n) || 0;
  if (count === 1) return '1 person or group reviews what changed and turns it on again.';
  return `${count} people and groups review what changed and turn it on again.`;
}

/** The changes-preview `changes` as [{ part, adds, removes }], empty parts left out. */
export function changeLines(changes) {
  return list(changes)
    .map((c) => ({
      part: c?.part || c?.tool_id || 'The plugin',
      adds: list(c?.added).filter(Boolean),
      removes: list(c?.removed).filter(Boolean),
    }))
    .filter((c) => c.adds.length || c.removes.length);
}

/** A person's name from a /users row. */
export function personName(u) {
  if (!u) return '';
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return full || u.username || u.email || u.user_id || '';
}

// ── Deliveries ──────────────────────────────────────────────────────────────

const DELIVERY_WORD = { processed: 'Done', pending: 'Not run yet', failed: 'Failed' };
export const deliveryWord = (status) => DELIVERY_WORD[status] || humanise(status) || 'Unknown';
export const deliveryTone = (status) => (status === 'processed' ? 'ok' : status === 'failed' ? 'bad' : 'warn');

// ── Response bodies ─────────────────────────────────────────────────────────

/** A response body as text to show: JSON pretty-printed, anything else as is. */
export function bodyText(body) {
  if (body === undefined || body === null || body === '') return '';
  if (typeof body === 'string') {
    try { return JSON.stringify(JSON.parse(body), null, 2); } catch { return body; }
  }
  try { return JSON.stringify(body, null, 2); } catch { return String(body); }
}
